import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { useVaultUploadState, VaultUploadStatus } from '@/models/vault'
import { useVault } from './VaultContext'
import {
  GAVE_UP_MESSAGE,
  GIVE_UP_READ_MS,
  REVIEWING_TOAST_ID,
  ReviewingToast,
  SLOW_READ_MS,
  UNREADABLE_MESSAGE,
} from './VaultUploadToast'
import { VaultUploadWatcher } from './VaultUploadWatcher'

/** One drop on the vault, from the moment its files are banked. */
export interface Drop {
  sourceId: string
  startedAt: number
}

/**
 * Drives one drop: holds the reviewing toast up, gives up on a read that never
 * lands, then hands each filing to a watcher. Renders nothing of its own.
 */
export function VaultDropProgress({ drop, onDone }: { drop: Drop; onDone: () => void }) {
  const { orgId } = useVault()
  const { state } = useVaultUploadState(orgId, drop.sourceId)
  const [settled, setSettled] = useState(0)
  const done = state?.status === VaultUploadStatus.DONE
  const filed = state?.filed ?? []
  const doneRef = useRef(onDone)
  doneRef.current = onDone

  // Owning up to a slow read, then abandoning it — both timed from the drop, not from this mount.
  useEffect(() => {
    if (done) return
    const elapsed = Date.now() - drop.startedAt
    const slow = setTimeout(
      () =>
        toast.custom(() => <ReviewingToast count={1} slow />, {
          id: REVIEWING_TOAST_ID,
          duration: Infinity,
        }),
      Math.max(0, SLOW_READ_MS - elapsed)
    )
    const giveUp = setTimeout(
      () => {
        toast.dismiss(REVIEWING_TOAST_ID)
        toast.error(GAVE_UP_MESSAGE)
        // Dropping it here unmounts this watcher, which is what stops the polling.
        doneRef.current()
      },
      Math.max(0, GIVE_UP_READ_MS - elapsed)
    )
    return () => {
      clearTimeout(slow)
      clearTimeout(giveUp)
    }
  }, [done, drop.startedAt])

  useEffect(() => {
    if (!done) return
    toast.dismiss(REVIEWING_TOAST_ID)
    // Read, but nothing in it the vault could file — the same dead end as a file we cannot open.
    if (filed.length === 0) {
      toast.error(UNREADABLE_MESSAGE)
      doneRef.current()
    }
  }, [done, filed.length])

  // Every filing has said where it landed; this drop is finished with.
  useEffect(() => {
    if (done && filed.length > 0 && settled >= filed.length) doneRef.current()
  }, [done, filed.length, settled])

  if (!done) return null
  return (
    <>
      {filed.map((documentId) => (
        <VaultUploadWatcher
          key={documentId}
          documentId={documentId}
          onSettled={() => setSettled((n) => n + 1)}
        />
      ))}
    </>
  )
}
